import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/admin`;

const UserEditModal = ({ user, onClose, onUpdate }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState(user?.status || 'active');
  const [reason, setReason] = useState('');
  const [durationType, setDurationType] = useState('days');
  const [durationValue, setDurationValue] = useState(7);
  const [customEndDate, setCustomEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(null);

  useEffect(() => {
    if (user) {
      fetchUserDetails();
    }
  }, [user]);

  const fetchUserDetails = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetails(response.data);
      
      // Set existing values
      setStatus(response.data.user.status || 'active');
      setReason(response.data.user.suspension_reason || response.data.user.banned_reason || '');
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleStatusUpdate = async () => {
    setLoading(true);
    try {
      const updateData = {
        status: status,
        reason: reason || null
      };

      if (status === 'suspended') {
        if (customEndDate) {
          updateData.custom_end_date = customEndDate;
        } else {
          const durationDays = durationType === 'days' ? durationValue : 
                             durationType === 'hours' ? Math.ceil(durationValue / 24) :
                             durationType === 'weeks' ? durationValue * 7 :
                             durationType === 'months' ? durationValue * 30 : durationValue;
          updateData.duration_days = durationDays;
        }
      }

      await axios.put(`${API}/users/${user.id}/status`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`User status updated to ${status}`);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!confirm('Send password reset email to this user?')) return;

    setLoading(true);
    try {
      await axios.post(`${API}/users/${user.id}/force-reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Password reset email sent to user');
    } catch (error) {
      console.error('Error forcing password reset:', error);
      alert('Failed to send password reset');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (userStatus) => {
    switch (userStatus) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'suspended': return 'text-yellow-600 bg-yellow-50';
      case 'banned': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              👤 Edit User: {user.username}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          {/* User Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">👤 Account Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <strong>Username:</strong> {user.username}
              </div>
              <div>
                <strong>Email:</strong> {user.email}
              </div>
              <div>
                <strong>Role:</strong> {user.role === 'admin' ? '👑 Admin' : '👤 User'}
              </div>
              <div>
                <strong>Created:</strong> {formatDate(user.created_at)}
              </div>
              <div>
                <strong>Last Active:</strong> {formatDate(user.last_active)}
              </div>
              <div>
                <strong>Login Count:</strong> {user.login_count}
              </div>
              {userDetails && (
                <div className="md:col-span-2">
                  <strong>Post Count:</strong> {userDetails.post_count}
                </div>
              )}
            </div>
          </div>

          {/* Password Management */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">🔐 Password Management</h3>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Password Status: ✅ Set (cannot view for security)
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    For security, passwords cannot be viewed. Use force reset to help users.
                  </p>
                </div>
                <button
                  onClick={handlePasswordReset}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  🔄 Force Reset
                </button>
              </div>
            </div>
          </div>

          {/* Account Status Management */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">⚖️ Account Status</h3>
            
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm font-medium">Current Status:</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(user.status)}`}>
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">New Status:</label>
                <div className="flex space-x-4">
                  {['active', 'suspended', 'banned'].map(statusOption => (
                    <label key={statusOption} className="flex items-center">
                      <input
                        type="radio"
                        name="status"
                        value={statusOption}
                        checked={status === statusOption}
                        onChange={(e) => setStatus(e.target.value)}
                        className="mr-2"
                      />
                      <span className={`px-2 py-1 rounded text-sm ${getStatusColor(statusOption)}`}>
                        {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Suspension Duration Options */}
              {status === 'suspended' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-3">⏱️ Suspension Duration</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="durationType"
                          checked={!customEndDate}
                          onChange={() => setCustomEndDate('')}
                          className="mr-2"
                        />
                        <span className="text-sm">Duration-based suspension</span>
                      </label>
                      {!customEndDate && (
                        <div className="ml-6 mt-2 flex items-center space-x-2">
                          <input
                            type="number"
                            min="1"
                            max="365"
                            value={durationValue}
                            onChange={(e) => setDurationValue(parseInt(e.target.value))}
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <select
                            value={durationType}
                            onChange={(e) => setDurationType(e.target.value)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                            <option value="weeks">Weeks</option>
                            <option value="months">Months</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="durationType"
                          checked={!!customEndDate}
                          onChange={() => setCustomEndDate(new Date().toISOString().split('T')[0])}
                          className="mr-2"
                        />
                        <span className="text-sm">Until specific date</span>
                      </label>
                      {customEndDate !== '' && (
                        <div className="ml-6 mt-2">
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Reason Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason {status !== 'active' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={`Enter reason for ${status === 'active' ? 'reactivation' : status}...`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                />
              </div>
            </div>
          </div>

          {/* Current Status Details */}
          {(user.suspension_reason || user.banned_reason) && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">📋 Current Status Details</h4>
              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                {user.suspension_reason && (
                  <div className="mb-2">
                    <strong>Suspension Reason:</strong> {user.suspension_reason}
                  </div>
                )}
                {user.suspension_end_date && (
                  <div className="mb-2">
                    <strong>Suspension Ends:</strong> {formatDate(user.suspension_end_date)}
                  </div>
                )}
                {user.banned_reason && (
                  <div className="mb-2">
                    <strong>Ban Reason:</strong> {user.banned_reason}
                  </div>
                )}
                {user.banned_date && (
                  <div className="mb-2">
                    <strong>Banned Date:</strong> {formatDate(user.banned_date)}
                  </div>
                )}
                {user.banned_by_admin && (
                  <div>
                    <strong>Banned By:</strong> {user.banned_by_admin}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recent Actions */}
          {userDetails?.recent_actions && userDetails.recent_actions.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">📋 Recent Admin Actions</h4>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2 text-sm">
                  {userDetails.recent_actions.slice(0, 5).map((action, index) => (
                    <div key={index} className="flex justify-between">
                      <span>{action.action}</span>
                      <span className="text-gray-500">{formatDate(action.timestamp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            ❌ Cancel
          </button>
          <button
            onClick={handleStatusUpdate}
            disabled={loading || (status !== 'active' && !reason.trim())}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '⏳ Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserEditModal;